import React, { useState } from "react";
import { ShieldAlert, ShieldCheck, Users, Info, AlertTriangle, ArrowRight, Eye, ShieldOff, Wrench, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "../../lib/matrix";
import { localizeAddon } from "../../lib/pricing";
import { buildRemediation } from "../../lib/remediation";
import usePlannerDataset from "../../hooks/usePlannerDataset";
import UserDrillSheet from "./UserDrillSheet";
import GapGroupsTable from "./GapGroupsTable";

const STATE = {
  enabled: { label: "Enforced", cls: "ok", icon: ShieldCheck },
  enabledForReportingButNotEnforced: { label: "Report-only", cls: "warn", icon: Eye },
  disabled: { label: "Disabled", cls: "", icon: ShieldOff },
};
const labels = (gaps, reqs) => reqs.map((r) => gaps.requirements[r]?.label || r).join(" + ");

export default function LicenseGaps({ summary, scanId, onShowUsers, onPlanFix, isAdmin }) {
  const gaps = summary.gaps;
  const { dataset, region, plans, addons, rows, pricing, symbol } = usePlannerDataset();
  const [drill, setDrill] = useState(null);
  const addonPrice = (name) => {
    const a = dataset?.addons.find((x) => x.name.toLowerCase() === (name || "").toLowerCase());
    return a ? localizeAddon(a, region) : null;
  };
  const planFix = (requirement, group = null) => {
    if (!dataset) return;
    const { moves, notes } = buildRemediation({ gaps: summary.gaps, plans, addons, rows, priceMode: "teams", requirement, group });
    if (!moves.length) return toast.error("No priced fix found", { description: notes.join("; ") || "The dataset has no plan or add-on covering this requirement." });
    notes.forEach((n) => toast.warning(n));
    const label = group ? `Planner pre-filled: ${group.sku_name} → ${labels(summary.gaps, group.missing)}` : requirement ? `Planner pre-filled: fix ${summary.gaps.requirements[requirement]?.label} gaps` : "Planner pre-filled: fix all license gaps";
    setDrill(null);
    onPlanFix?.(moves, label);
    return undefined;
  };
  const canFix = isAdmin && !!onPlanFix;
  const drillRequirement = (r) =>
    setDrill({
      title: `${r.users.toLocaleString()} user${r.users === 1 ? "" : "s"} need ${r.label}`,
      description: r.why,
      params: { filter: "gaps", req: r.key },
      fix: canFix ? { label: `Fix ${r.label} in planner`, run: () => planFix(r.key) } : null,
    });
  const drillPolicy = (p) =>
    p.gap > 0 &&
    setDrill({
      title: p.name,
      description: `${p.gap.toLocaleString()} of ${p.in_scope.toLocaleString()} users in scope of this policy lack ${labels(gaps, p.requires)}.`,
      params: { filter: "gaps", policy: p.id },
    });
  const drillGroup = (g) =>
    setDrill({
      title: `${g.sku_name} → missing ${labels(gaps, g.missing)}`,
      description: `${g.users.toLocaleString()} user${g.users === 1 ? "" : "s"} on ${g.sku_name} are targeted by policies that need ${labels(gaps, g.missing)}.`,
      params: { filter: "gaps", sku: g.from_sku_id || "__unlicensed__", missing: g.missing.join(",") },
      fix: canFix ? { label: "Fix this group in planner", run: () => planFix(null, g) } : null,
    });

  if (!gaps) {
    return (
      <section className="ws-card glass" data-testid="gaps-unavailable">
        <h2>
          <ShieldAlert size={16} /> License gaps
        </h2>
        <div className="ws-alert">
          <Info size={14} /> {summary.source === "csv" ? "This tenant was imported from a CSV export. Conditional Access policies - and therefore the license-gap check - need a Microsoft Graph connection: link the tenant with an Entra app registration to enable it." : summary.ca ? "This scan predates the gap analysis - run a new scan to check policies against licenses." : "Conditional Access policies were not read. Grant Policy.Read.All (and optionally GroupMember.Read.All) to the app registration and re-run the scan."}
        </div>
      </section>
    );
  }

  const reqs = Object.entries(gaps.requirements);
  const active = gaps.policies.filter((p) => !p.skipped);
  const remediation = reqs.map(([key, r]) => {
    const n = gaps.by_requirement[key] || 0;
    const addon = addonPrice(r.addon);
    const unit = addon?.price ?? null;
    return { key, ...r, users: n, addon, unit, monthly: unit === null || !n ? null : unit * n };
  });
  const totalMonthly = remediation.reduce((s, r) => s + (r.monthly || 0), 0);

  return (
    <div className="ws-stack" data-testid="license-gaps">
      <div className="kpi-grid gaps">
        <div className={`kpi glass ${gaps.users_with_gaps ? "warn clickable" : "ok"}`} onClick={() => gaps.users_with_gaps && setDrill({ title: `${gaps.users_with_gaps.toLocaleString()} users with a license gap`, description: "Every user targeted by an active Conditional Access policy whose license does not include the required Entra ID / Intune entitlement.", params: { filter: "gaps" }, fix: canFix ? { label: "Fix all in planner", run: () => planFix(null) } : null })} data-testid="gaps-total">
          <span className="kpi-ico">{gaps.users_with_gaps ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />}</span>
          <div>
            <div className="kpi-value mono">{gaps.users_with_gaps.toLocaleString()}</div>
            <div className="kpi-label">Users with a license gap</div>
            <div className="kpi-sub">
              {active.length} active polic{active.length === 1 ? "y" : "ies"} checked · {gaps.policies.length - active.length} disabled skipped
            </div>
            {gaps.users_with_gaps > 0 && (
              <span className="kpi-link">
                View users <ChevronRight size={12} />
              </span>
            )}
          </div>
        </div>
        {remediation.map((r) => (
          <div key={r.key} className={`kpi glass ${r.users ? "warn clickable" : ""}`} onClick={() => r.users && drillRequirement(r)} data-testid={`gap-req-${r.key}`}>
            <span className="kpi-ico">
              <Users size={16} />
            </span>
            <div>
              <div className="kpi-value mono">{r.users.toLocaleString()}</div>
              <div className="kpi-label">need {r.label}</div>
              <div className="kpi-sub">
                {r.users ? (
                  <>
                    Fix: {r.addon?.name || r.addon} {r.unit !== null && r.unit !== undefined ? `${formatPrice(r.unit, symbol, r.addon?.estimated)} / user` : ""}
                    {r.monthly !== null && <b className="mono"> ≈ {formatPrice(r.monthly, symbol, r.addon?.estimated)} / mo</b>}
                  </>
                ) : (
                  "no gap"
                )}
              </div>
              {r.users > 0 && (
                <span className="kpi-actions">
                  <button type="button" className="tb-btn sm kpi-action" onClick={(e) => (e.stopPropagation(), drillRequirement(r))} data-testid={`gap-drill-${r.key}`}>
                    <Users size={12} /> View users
                  </button>
                  {canFix && (
                    <button type="button" className="tb-btn sm kpi-action" onClick={(e) => (e.stopPropagation(), planFix(r.key))} disabled={!dataset} data-testid={`gap-fix-${r.key}`}>
                      <Wrench size={12} /> Plan fix
                    </button>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {gaps.users_with_gaps > 0 && (
        <div className="ws-alert warn" data-testid="gaps-summary">
          <AlertTriangle size={14} />
          <span>
            Closing every gap with standalone add-ons costs about <b className="mono">{formatPrice(totalMonthly, symbol)} / mo</b> at {pricing.label || "US"} list price. Moving the affected users to a suite that includes the feature (e.g. E3 → E5 for Entra ID P2) is often cheaper - model it in the planner.
          </span>
          <span className="alert-actions">
            {canFix && (
              <button type="button" className="tb-btn sm primary" onClick={() => planFix(null)} disabled={!dataset} data-testid="gaps-fix-all">
                <Wrench size={12} /> Fix all in planner
              </button>
            )}
            {onShowUsers && (
              <button type="button" className="tb-btn sm" onClick={onShowUsers} data-testid="gaps-show-users">
                Show affected users <ArrowRight size={12} />
              </button>
            )}
          </span>
        </div>
      )}
      {(gaps.unresolved_groups > 0 || gaps.groups_error) && (
        <div className="ws-alert" data-testid="gaps-groups-warning">
          <Info size={14} /> {gaps.unresolved_groups} group-targeted polic{gaps.unresolved_groups === 1 ? "y" : "ies"} could not be resolved to users. Grant <b>GroupMember.Read.All</b> to the app registration and re-run the scan. {gaps.groups_error && <span className="mono small">({gaps.groups_error})</span>}
        </div>
      )}

      <GapGroupsTable gaps={gaps} onDrill={drillGroup} onFix={canFix ? (g) => planFix(null, g) : null} isAdmin={isAdmin} />

      <section className="ws-card glass">
        <div className="ws-card-head">
          <h2>Policies vs licenses</h2>
          <span className="muted small">Rules: any Conditional Access policy → Entra ID P1 · sign-in / user risk → Entra ID P2 · require compliant device → Intune Plan 1. Guests, disabled accounts and dormant unlicensed accounts (no sign-in in 90 days, e.g. shared mailboxes) are excluded. Click a policy with a gap to list the affected users.</span>
        </div>
        <div className="ws-table-wrap">
          <table className="ws-table" data-testid="gaps-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>State</th>
                <th>Requires</th>
                <th className="num">In scope</th>
                <th className="num">Covered</th>
                <th className="num">Gap</th>
                <th>Notes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {gaps.policies.map((p) => {
                const st = STATE[p.state] || { label: p.state, cls: "", icon: Info };
                return (
                  <tr key={p.id} className={`${p.gap ? "has-gap clickable" : ""}`} onClick={() => drillPolicy(p)} data-testid={`gap-policy-${p.id}`}>
                    <td className="sku-name">{p.name}</td>
                    <td>
                      <span className={`status-pill ${st.cls}`}>
                        <st.icon size={11} /> {st.label}
                      </span>
                    </td>
                    <td>
                      <div className="lic-chips">
                        {p.requires.map((r) => (
                          <span key={r} className={`mini-chip ${p.gap_by_requirement?.[r] ? "warn" : ""}`}>
                            {gaps.requirements[r]?.label || r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="num mono">{p.skipped ? "—" : p.in_scope.toLocaleString()}</td>
                    <td className="num mono ok">{p.skipped ? "—" : p.covered.toLocaleString()}</td>
                    <td className={`num mono ${p.gap ? "warn" : ""}`} data-testid={`gap-count-${p.id}`}>
                      {p.skipped ? "—" : p.gap.toLocaleString()}
                    </td>
                    <td className="muted small">
                      {p.skipped && "Disabled policy - not evaluated."}
                      {p.scope_unknown && <span className="warn">Scope incomplete: {p.unresolved_groups.length} unresolved group(s). </span>}
                      {p.gap > 0 && p.sample?.length > 0 && <span>e.g. {p.sample.slice(0, 3).map((s) => s.upn).join(", ")}</span>}
                    </td>
                    <td className="num">
                      {p.gap > 0 && (
                        <button type="button" className="tb-btn sm" onClick={(e) => (e.stopPropagation(), drillPolicy(p))} data-testid={`gap-policy-drill-${p.id}`}>
                          <Users size={12} /> {p.gap.toLocaleString()}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <UserDrillSheet scanId={scanId} summary={summary} drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
